/**
 * SelectableTextField Component
 *
 * A TextField wrapper that detects text selection and fires a callback.
 * Used for the "teach by example" pattern learning feature.
 */

import React, { useRef } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

export interface TextSelection {
  start: number;
  end: number;
  text: string;
}

interface SelectableTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onTextSelect?: (selection: TextSelection, event: React.MouseEvent<HTMLInputElement>) => void;
}

/**
 * TextField that detects text selection and fires callback
 */
export const SelectableTextField: React.FC<SelectableTextFieldProps> = ({
  value,
  onChange,
  onTextSelect,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleMouseUp = (event: React.MouseEvent<HTMLInputElement>) => {
    if (!onTextSelect || !inputRef.current) return;

    const start = inputRef.current.selectionStart || 0;
    const end = inputRef.current.selectionEnd || 0;

    // Only fire callback if there's an actual selection (not just a cursor position)
    if (start !== end && end > start) {
      const selectedText = value.substring(start, end);

      // Don't fire for whitespace-only selections
      if (selectedText.trim().length > 0) {
        onTextSelect(
          {
            start,
            end,
            text: selectedText,
          },
          event
        );
      }
    }
  };

  return (
    <TextField
      {...props}
      value={value}
      onChange={onChange}
      inputRef={inputRef}
      onMouseUp={handleMouseUp}
      // Also handle touch events for mobile
      onTouchEnd={(e) => {
        // Cast to MouseEvent for compatibility
        handleMouseUp(e as unknown as React.MouseEvent<HTMLInputElement>);
      }}
    />
  );
};

export default SelectableTextField;
